import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BulkImport from "@/components/BulkImport";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";

export default function TypingAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", language: "en", content: "", difficulty: "medium" });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/typing-passages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/admin/typing-passages/${editingId}`, form);
        toast.success("Typing passage updated");
      } else {
        await api.post("/admin/typing-passages", form);
        toast.success("Typing passage added");
      }
      setForm({ title: "", language: "en", content: "", difficulty: "medium" });
      setEditingId(null);
      load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const editTyping = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title || "", language: p.language || "en",
      content: p.content || "", difficulty: p.difficulty || "medium",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelTypingEdit = () => {
    setEditingId(null);
    setForm({ title: "", language: "en", content: "", difficulty: "medium" });
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/typing-passages/${id}`);
    if (editingId === id) cancelTypingEdit();
    load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={add} className="border border-black/20 p-6 space-y-4" data-testid="admin-typing-form">
        <div className="text-xs uppercase tracking-[0.3em] font-bold">Add typing passage</div>
        <Input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none font-mono" data-testid="admin-typing-title" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">Language</Label>
            <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
              <SelectTrigger className="rounded-none mt-1" data-testid="admin-typing-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="hi">हिन्दी (Hindi)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold">Difficulty</Label>
            <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
              <SelectTrigger className="rounded-none mt-1" data-testid="admin-typing-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Textarea required placeholder="Passage text (English or Hindi)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`rounded-none min-h-40 ${form.language === "hi" ? "text-lg" : "font-mono"}`} lang={form.language} data-testid="admin-typing-content" />
        <Button type="submit" disabled={busy} className={`rounded-none w-full h-11 ${editingId ? "bg-brand-crimson hover:bg-brand-crimson/90" : "bg-[#0A2540] hover:bg-[#002FA7]"} text-white text-xs uppercase tracking-[0.2em] font-bold`} data-testid="admin-typing-submit-btn">
          {busy ? (editingId ? "Updating…" : "Adding…") : (editingId ? "Update passage" : "Add passage")}
        </Button>
        {editingId && (
          <Button type="button" onClick={cancelTypingEdit} variant="outline" className="rounded-none w-full h-9 border-black/30 text-neutral-700 hover:bg-neutral-100 text-[10px] uppercase tracking-widest font-bold" data-testid="admin-typing-cancel-edit-btn">
            Cancel edit
          </Button>
        )}
      </form>

      <div className="md:col-span-2 lg:col-span-2">
        <BulkImport
          module="typing"
          requiredFields={["title", "content"]}
          optionalFields={["language", "difficulty"]}
          onImported={load}
        />
      </div>

      <div>
        <h3 className="text-lg font-black tracking-tight mb-3">Existing typing passages</h3>
        <div className="border border-black/15 divide-y divide-black/10" data-testid="admin-typing-list">
          {items.length === 0 && <div className="p-4 font-mono text-sm text-neutral-500">None yet.</div>}
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between p-4">
              <div>
                <div className="font-bold">{p.title}</div>
                <div className="text-xs font-mono text-neutral-500">{p.language === "hi" ? "हिन्दी" : "English"} · {p.difficulty} · {p.word_count} words</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => editTyping(p)} variant="outline" size="sm" className="rounded-none border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white" data-testid={`admin-typing-edit-${p.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button onClick={() => del(p.id)} variant="outline" size="sm" className="rounded-none border-red-300 text-red-700 hover:bg-red-50" data-testid={`admin-typing-delete-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
